import { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface AiSuggestion {
  tariffNumber?: string;
  resource?: string;
}

interface TariffAiHelperProps {
  onSuggestion: (suggestion: AiSuggestion) => void;
}

export function TariffAiHelper({ onSuggestion }: TariffAiHelperProps) {
  const { t } = useTranslation('carm');
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const result = await api.post<{ reply: string; suggestion?: AiSuggestion }>(
        '/v4/carm/ai-helper',
        { message: userMessage, history: messages.slice(-6) },
      );
      setMessages((prev) => [...prev, { role: 'assistant', content: result.reply }]);
      if (result.suggestion?.tariffNumber) onSuggestion(result.suggestion);
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', content: t('ai.error') }]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 text-xs rounded-lg border border-dashed border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-950/50 transition-colors"
      >
        <Sparkles className="h-3.5 w-3.5" />
        {t('ai.trigger')}
      </button>
    );
  }

  const examples = [
    t('ai.examples.sensors'),
    t('ai.examples.batteries'),
    t('ai.examples.enclosures'),
    t('ai.examples.pcb'),
  ];

  return (
    <div className="border border-purple-200 dark:border-purple-800 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-purple-50 dark:bg-purple-950/30 border-b border-purple-200 dark:border-purple-800">
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-purple-600" />
          <span className="text-xs font-medium text-purple-800 dark:text-purple-200">{t('ai.title')}</span>
        </div>
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-purple-500 hover:text-purple-700">
          {t('ai.close')}
        </button>
      </div>

      <div className="max-h-64 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-xs text-muted-foreground space-y-2">
            <p>{t('ai.intro')}</p>
            <div className="flex flex-wrap gap-1.5">
              {examples.map((example) => (
                <button
                  key={example}
                  type="button"
                  onClick={() => setInput(example)}
                  className="px-2 py-1 text-[11px] rounded-md border bg-background hover:bg-muted transition-colors"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`text-xs ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
            <div className={`inline-block max-w-[85%] px-3 py-2 rounded-lg whitespace-pre-wrap ${
              msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'
            }`}>
              {msg.content}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t('ai.thinking')}
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2 p-2 border-t">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('ai.placeholder')}
          className="h-8 text-xs"
          disabled={loading}
        />
        <Button type="submit" size="sm" className="h-8 px-3" disabled={loading || !input.trim()}>
          <Send className="h-3 w-3" />
        </Button>
      </form>
    </div>
  );
}
